import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { AUTH_API_URL, API_BASE_URL } from '../config/env';
import { setAccessToken, clearAccessToken, getCsrfToken } from '../lib/tokenStore';

const AuthContext = createContext(null);

/**
 * AuthProvider — wraps the entire app and manages auth state.
 *
 * Token strategy:
 *  - accessToken  → in-memory only (tokenStore.js), never localStorage
 *  - refreshToken → HttpOnly cookie (vetcollab_rt), managed by the browser
 *  - csrfToken    → non-HttpOnly cookie (vetcollab_csrf), read via document.cookie
 *  - user object  → localStorage ONLY for non-sensitive UI state (name, role)
 *
 * On every page load, `bootstrapAuth()` fires:
 *  1. It calls POST /auth/refresh using the HttpOnly cookie automatically sent by the browser.
 *  2. On success → stores new access token in-memory and user in localStorage.
 *  3. On failure → clears all storage (user is logged out).
 */
const getMergedUserWithLocalOverrides = (fetchedUser) => {
  if (!fetchedUser) return null;
  try {
    const rawOverrides = localStorage.getItem('vetcollab_user_profile');
    if (rawOverrides) {
      const parsed = JSON.parse(rawOverrides);
      if (parsed) {
        return { ...fetchedUser, ...parsed };
      }
    }
  } catch (e) {
    console.warn('[AuthContext] Failed to parse vetcollab_user_profile:', e);
  }
  return fetchedUser;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const bootstrapped = useRef(false);

  const bootstrapAuth = useCallback(async () => {
    // If not authenticated and no csrf token is present, skip silent refresh
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    const csrf = getCsrfToken();
    if (!isAuthenticated && !csrf) {
      setIsAuthReady(true);
      return;
    }

    try {
      const csrf = getCsrfToken();
      const res = await fetch(`${AUTH_API_URL}/refresh`, {
        method: 'POST',
        credentials: 'include', // sends vetcollab_rt HttpOnly cookie automatically
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'x-csrf-token': csrf } : {}),
        },
      });

      if (!res.ok) throw new Error('Refresh failed');

      const json = await res.json();
      const { accessToken, csrfToken } = json.data;

      setAccessToken(accessToken);
      if (csrfToken) localStorage.setItem('csrf', csrfToken);
      
      const userRes = await fetch(`${API_BASE_URL}/users/me`, {
        headers: { 
          'Authorization': `Bearer ${accessToken}`,
          ...(csrfToken ? { 'x-csrf-token': csrfToken } : {})
        }
      });
      if (!userRes.ok) throw new Error('Failed to fetch user profile');
      const userJson = await userRes.json();
      
      setUser(getMergedUserWithLocalOverrides(userJson.data));
    } catch {
      // Silent refresh failed — session is expired or invalid. Force logout.
      clearAccessToken();
      localStorage.removeItem('isAuthenticated');
      setUser(null);
    } finally {
      setIsAuthReady(true);
    }
  }, []);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    bootstrapAuth();
  }, [bootstrapAuth]);

  /** Called by LoginForm, MagicAuthPage, CreatePassword after a successful auth response. */
  const login = useCallback((accessToken, userData, csrfToken) => {
    setAccessToken(accessToken);
    // Only store non-sensitive auth flag in localStorage
    localStorage.setItem('isAuthenticated', 'true');
    if (csrfToken) localStorage.setItem('csrf', csrfToken);
    
    // Remove the old insecure keys if they exist
    localStorage.removeItem('access');
    setUser(getMergedUserWithLocalOverrides(userData));
  }, []);

  /** Updates the user object in state and persists overrides to localStorage */
  const updateUser = useCallback((newUserData) => {
    setUser(prevUser => {
      const merged = { ...prevUser, ...newUserData };
      try {
        localStorage.setItem('vetcollab_user_profile', JSON.stringify(merged));
      } catch (e) {
        console.warn('[AuthContext] Failed to persist profile overrides:', e);
      }
      return merged;
    });
  }, []);

  /** Calls the backend logout endpoint and clears all local state. */
  const logout = useCallback(async () => {
    try {
      const csrf = getCsrfToken();
      await fetch(`${AUTH_API_URL}/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'x-csrf-token': csrf } : {}),
        },
      });
    } catch { /* ignore — we always clear local state */ }

    clearAccessToken();
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('access');
    localStorage.removeItem('csrf');
    localStorage.removeItem('vetcollab_user_profile');
    setUser(null);
    window.location.href = '/login';
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthReady, login, logout, updateUser }}>
      {/* Don't render routes until we've attempted the silent refresh */}
      {isAuthReady ? children : (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
